import React from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";

interface ErrorItem {
  code: number;
  message: string;
  source?: string;
}

interface ExceptionItem {
  code: number;
  genericMessage: string;
  genericStackTrace?: string;
  source?: string;
}

interface QuoteErrorDetails {
  insurer: string;
  duration?: number;
  errorList?: ErrorItem[];
  exceptionList?: ExceptionItem[];
  hasErrors?: boolean;
  securitySystem?: string;
  bonusMalus?: string;
  insurerRequest?: string[];
  insurerResponse?: string[];
}

interface ErrorDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quotes: QuoteErrorDetails[];
  title?: string;
  showDuration?: boolean;
  showSecurityRequirements?: boolean;
  showBonusMalus?: boolean;
  showApiDetails?: boolean;
}

export const ErrorDetailsDialog: React.FC<ErrorDetailsDialogProps> = ({
  open,
  onOpenChange,
  quotes,
  title = "Errors and Security Requirements",
  showDuration = false,
  showSecurityRequirements = false,
  showBonusMalus = false,
  showApiDetails = false,
}) => {
  const hasAnyErrorsOrExceptions = quotes.some(
    (q) =>
      (q.errorList && q.errorList.length > 0) ||
      (q.exceptionList && q.exceptionList.length > 0) ||
      (showSecurityRequirements && q.securitySystem) ||
      (showBonusMalus && q.bonusMalus)
  );

  return (
    <Dialog open={open} onOpenChange={(e, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: "800px", maxHeight: "80vh" }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent
            style={{ padding: "20px", overflowY: "auto", maxHeight: "60vh" }}
          >
            {quotes
              .map((quote) => {
                const errors = quote.errorList || [];
                const exceptions = quote.exceptionList || [];
                const security = showSecurityRequirements
                  ? quote.securitySystem
                  : undefined;
                const bonusMalus = showBonusMalus
                  ? quote.bonusMalus
                  : undefined;

                if (
                  errors.length === 0 &&
                  !security &&
                  exceptions.length === 0 &&
                  !bonusMalus
                )
                  return null;

                return (
                  <div
                    key={quote.insurer}
                    style={{
                      marginBottom: "24px",
                      borderBottom: "1px solid #e1e1e1",
                      paddingBottom: "16px",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 12px 0",
                        color: "#323130",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {quote.insurer}
                      {showDuration && quote.duration && (
                        <span
                          style={{
                            marginLeft: "12px",
                            fontSize: "12px",
                            fontWeight: "normal",
                            color: "#666",
                          }}
                        >
                          ({(quote.duration / 1000).toFixed(2)}s)
                        </span>
                      )}
                    </h4>

                    {errors.length > 0 && (
                      <div style={{ marginBottom: "12px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "12px",
                            marginBottom: "8px",
                            color: "#d13438",
                          }}
                        >
                          Errors ({errors.length}):
                        </div>
                        <div
                          style={{
                            background: quote.hasErrors ? "#f8d7da" : "#fff3cd",
                            padding: "12px",
                            borderRadius: "4px",
                            border: `1px solid ${
                              quote.hasErrors ? "#f5c6cb" : "#ffeeba"
                            }`,
                          }}
                        >
                          {errors.map((err, idx) => (
                            <div
                              key={idx}
                              style={{
                                fontSize: "11px",
                                marginBottom:
                                  idx < errors.length - 1 ? "8px" : "0",
                                paddingBottom:
                                  idx < errors.length - 1 ? "8px" : "0",
                                borderBottom:
                                  idx < errors.length - 1
                                    ? "1px solid rgba(0,0,0,0.1)"
                                    : "none",
                                color: quote.hasErrors ? "#721c24" : "#856404",
                                lineHeight: "1.4",
                              }}
                            >
                              <span style={{ fontWeight: "600" }}>
                                Code {err.code}:
                              </span>{" "}
                              {err.source && `[${err.source}] `}
                              {err.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {exceptions.length > 0 && (
                      <div style={{ marginBottom: "12px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "12px",
                            marginBottom: "8px",
                            color: "#d13438",
                          }}
                        >
                          Exceptions ({exceptions.length}):
                        </div>
                        <div
                          style={{
                            background: "#fff0f0",
                            padding: "12px",
                            borderRadius: "4px",
                            border: "1px solid #ffccc7",
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {exceptions.map((ex, idx) => (
                            <div
                              key={idx}
                              style={{
                                fontSize: "10px",
                                marginBottom:
                                  idx < exceptions.length - 1 ? "12px" : "0",
                                paddingBottom:
                                  idx < exceptions.length - 1 ? "12px" : "0",
                                borderBottom:
                                  idx < exceptions.length - 1
                                    ? "1px solid rgba(0,0,0,0.1)"
                                    : "none",
                                color: "#721c24",
                                lineHeight: "1.4",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                }}
                              >
                                Exception Code {ex.code}:
                                {ex.source && ` [${ex.source}]`}
                              </div>
                              <div style={{ marginBottom: "4px" }}>
                                {ex.genericMessage}
                              </div>
                              {ex.genericStackTrace && (
                                <details style={{ marginTop: "4px" }}>
                                  <summary
                                    style={{
                                      cursor: "pointer",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Stack Trace
                                  </summary>
                                  <pre
                                    style={{
                                      fontSize: "9px",
                                      whiteSpace: "pre-wrap",
                                      marginTop: "4px",
                                      padding: "8px",
                                      background: "#fff",
                                      borderRadius: "2px",
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {ex.genericStackTrace}
                                  </pre>
                                </details>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {security && (
                      <div style={{ marginBottom: "12px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "12px",
                            marginBottom: "8px",
                            color: "#1e3a8a",
                          }}
                        >
                          Security Requirements:
                        </div>
                        <div
                          style={{
                            background: "#e0f2fe",
                            padding: "12px",
                            borderRadius: "4px",
                            border: "1px solid #bae6fd",
                            fontSize: "11px",
                            lineHeight: "1.6",
                            color: "#1e3a8a",
                          }}
                        >
                          {security}
                        </div>
                      </div>
                    )}

                    {bonusMalus && (
                      <div style={{ marginBottom: "12px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "12px",
                            marginBottom: "8px",
                            color: "#1e3a8a",
                          }}
                        >
                          Bonus-Malus:
                        </div>
                        <div
                          style={{
                            background: "#e0f2fe",
                            padding: "12px",
                            borderRadius: "4px",
                            border: "1px solid #bae6fd",
                            fontSize: "11px",
                            lineHeight: "1.6",
                            color: "#1e3a8a",
                          }}
                        >
                          {bonusMalus}
                        </div>
                      </div>
                    )}

                    {showApiDetails &&
                      quote.insurerRequest &&
                      quote.insurerRequest.length > 0 && (
                        <div style={{ marginBottom: "12px" }}>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "12px",
                              marginBottom: "8px",
                              color: "#0078d4",
                            }}
                          >
                            API Requests ({quote.insurerRequest.length}):
                          </div>
                          <div
                            style={{
                              background: "#f0f6ff",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #d1e7ff",
                              fontSize: "10px",
                              maxHeight: "100px",
                              overflowY: "auto",
                            }}
                          >
                            {quote.insurerRequest.map((url, idx) => (
                              <div key={idx} style={{ marginBottom: "4px" }}>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#0078d4",
                                    textDecoration: "none",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {url.split("/").pop() || url}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {showApiDetails &&
                      quote.insurerResponse &&
                      quote.insurerResponse.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "12px",
                              marginBottom: "8px",
                              color: "#0078d4",
                            }}
                          >
                            API Responses ({quote.insurerResponse.length}):
                          </div>
                          <div
                            style={{
                              background: "#f0f6ff",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #d1e7ff",
                              fontSize: "10px",
                              maxHeight: "100px",
                              overflowY: "auto",
                            }}
                          >
                            {quote.insurerResponse.map((url, idx) => (
                              <div key={idx} style={{ marginBottom: "4px" }}>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#0078d4",
                                    textDecoration: "none",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {url.split("/").pop() || url}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                );
              })
              .filter(Boolean)}

            {!hasAnyErrorsOrExceptions && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                }}
              >
                <InfoRegular
                  style={{
                    fontSize: "48px",
                    marginBottom: "16px",
                    opacity: 0.3,
                  }}
                />
                <div>No errors or security requirements to display</div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
